/**
 * favoritesStore — Zustand store for marketplace favorites.
 * Single source of truth: hydrated on login, kept in sync across pages.
 * Optimistic updates with automatic rollback on API error.
 */
import { create } from 'zustand';
import {
  listFavoritePlants,
  addFavoritePlant,
  removeFavoritePlant,
  addFavoritePlantsBatch,
} from '../api/favorites';

interface FavoritesState {
  ids: Set<string>;
  names: Record<string, string>;
  loading: boolean;
  initialized: boolean;

  /** Load / refresh favorites from the server. */
  hydrate: () => Promise<void>;

  /** Toggle a single favorite; optimistic with rollback. */
  toggle: (plantId: string) => Promise<void>;

  /** Add multiple favorites at once; optimistic. */
  addBatch: (plantIds: string[]) => Promise<{ count: number }>;

  /** Cache plant display name for favorites popover. */
  setName: (plantId: string, name: string) => void;
}

export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  ids: new Set<string>(),
  names: {},
  loading: false,
  initialized: false,

  hydrate: async () => {
    set({ loading: true });
    try {
      const plants = await listFavoritePlants();
      const ids = plants.map((plant) => plant.id);
      const names = plants.reduce<Record<string, string>>((acc, plant) => {
        if (plant.name) acc[plant.id] = plant.name;
        return acc;
      }, {});
      set((s) => ({ ids: new Set(ids), names: { ...s.names, ...names }, initialized: true }));
    } catch {
      // Keep existing ids; not a fatal error
    } finally {
      set({ loading: false });
    }
  },

  toggle: async (plantId: string) => {
    const prev = new Set(get().ids);
    const isFav = prev.has(plantId);

    // Optimistic update
    set((s) => {
      const next = new Set(s.ids);
      if (isFav) next.delete(plantId);
      else next.add(plantId);
      return { ids: next };
    });

    try {
      if (isFav) {
        await removeFavoritePlant(plantId);
      } else {
        await addFavoritePlant(plantId);
      }
    } catch {
      // Rollback
      set({ ids: prev });
      throw new Error(isFav ? 'Failed to remove favorite' : 'Failed to add favorite');
    }
  },

  addBatch: async (plantIds: string[]) => {
    const prev = new Set(get().ids);

    // Optimistic update
    set((s) => {
      const next = new Set(s.ids);
      for (const id of plantIds) next.add(id);
      return { ids: next };
    });

    try {
      const res = await addFavoritePlantsBatch(plantIds);
      return { count: res.count ?? plantIds.length };
    } catch {
      set({ ids: prev });
      throw new Error('Failed to batch favorite assets');
    }
  },

  setName: (plantId, name) => set((s) => ({
    names: s.names[plantId] === name ? s.names : { ...s.names, [plantId]: name },
  })),
}));
